import { useMemo } from "react";
import { Link } from "wouter";
import { useNodes } from "../../api/queries";
import type { Node } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { HealthStatePill } from "../../components/StatePill";
import { PageHeader } from "../../components/PageHeader";
import { QueryStates } from "../../components/QueryStates";
import { type HealthRow, useHealthRows } from "./useHealthRows";
import "./health.css";

/**
 * Fleet-wide health matrix (ADR-0030 P4-2): one row per placement — the
 * service/node pair — with its rolled-up health. A true services x nodes
 * grid was considered (per the PR brief) and set aside in favor of this flat
 * table: the fixture fleet alone spans 4 nodes and grows unbounded with
 * every project, so a grid's column count has no natural ceiling, while a
 * flat table stays exactly as readable at any fleet size and reuses the
 * same `DataTable` chrome every other list view in this app already does.
 * Read-only — this is a DERIVED VIEW ONLY (charter A-5, see HealthCell's
 * doc comment in types.ts); there is no write surface on this page.
 *
 * The "Node" column is rendered as "Machine" (matching the Machines page's
 * user-facing vocabulary) and resolves each placement's raw node id to that
 * node's name, same pattern as `DeployInFlightCard` — the id lives on as the
 * link's `title` for anyone who needs to correlate it with logs/CLI output.
 */
function buildColumns(linksDegraded: boolean, nodesById: Map<string, Node>): DataTableColumn<HealthRow>[] {
  return [
    {
      key: "service",
      header: "Service",
      cell: (row) =>
        row.projectId ? (
          <Link
            className="data-table__primary-link"
            href={`/projects/${row.projectId}/services/${row.serviceId}`}
          >
            {row.serviceName}
          </Link>
        ) : (
          row.serviceName
        ),
      className: "data-table__primary",
    },
    {
      key: "node",
      header: "Machine",
      cell: (row) => (
        <Link className="data-table__mono" href={`/hosts/${row.nodeId}`} title={row.nodeId}>
          {nodesById.get(row.nodeId)?.name ?? row.nodeId}
        </Link>
      ),
    },
    {
      key: "health",
      header: "Health",
      cell: (row) => <HealthStatePill health={row.health} />,
    },
    {
      key: "detail",
      header: "Detail",
      cell: (row) => row.detail || (linksDegraded ? "" : "—"),
      className: "health-list__detail",
    },
  ];
}

export function HealthPage() {
  const { rows, isPending, isError, error, linksDegraded, refetch } = useHealthRows();
  const { data: nodesPage } = useNodes({ limit: 200 });
  const nodesById = useMemo(() => new Map(nodesPage?.items.map((n) => [n.id, n])), [nodesPage]);
  const columns = buildColumns(linksDegraded, nodesById);

  return (
    <>
      <PageHeader
        title="Health"
        description="Every service running on a machine in this fleet, and its rolled-up health."
      />

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading health…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error
            ? error.message
            : "Couldn't load the health matrix. Check your connection and try again."
        }
        isEmpty={rows.length === 0}
        empty={
          <EmptyState
            title="Nothing running yet"
            description="Add a service to a machine to see its health here."
          />
        }
      >
        {linksDegraded && (
          <p className="health-list__warning" role="status">
            Couldn't resolve every service's project — some service names below aren't linked.
            Health itself is unaffected.
          </p>
        )}
        <DataTable columns={columns} rows={rows} rowKey={(row) => row.placementId} />
      </QueryStates>
    </>
  );
}
